import { useQuery } from '@tanstack/react-query'
import { createFileRoute, type SearchSchemaInput, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import {
  type CompanyReportRow,
  companyReportQueryOptions,
  currentMonthRange,
  type EmployeeReportRow,
  employeeReportQueryOptions,
  fetchReportCsv,
  formatCallTime,
  formatRangeLabel,
  isReportDate,
  normalizeRange,
  type ProjectReportRow,
  projectReportQueryOptions,
  type ReportRange,
  type ReportSliceName,
  type ReportTotals,
  reportCsvFilename,
  sameRange,
} from '@/entities/report'
import { useRequireSession } from '@/entities/session'
import { PeriodPicker } from '@/features/pick-report-period'
import { cn, pluralRu, saveBlob } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Icon } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import { toast } from '@/shared/ui/toast'
import { type ReportItem, ReportView } from '@/widgets/report-view'

// Три среза одного вопроса «куда ушло время»: субъект строки меняется, период и
// вид — общие. Слово субъекта нужно склонениям итога («12 сотрудников»).
const TABS = [
  {
    value: 'employees',
    label: 'Сотрудники',
    word: ['сотрудник', 'сотрудника', 'сотрудников'],
    empty: 'За период никто не был на созвонах',
  },
  {
    value: 'projects',
    label: 'Проекты',
    word: ['проект', 'проекта', 'проектов'],
    empty: 'За период нет встреч по проектам',
  },
  {
    value: 'companies',
    label: 'Компании',
    word: ['компания', 'компании', 'компаний'],
    empty: 'За период нет встреч по компаниям',
  },
] as const satisfies readonly {
  value: ReportSliceName
  label: string
  word: readonly [string, string, string]
  empty: string
}[]

interface ReportsSearch {
  slice?: 'projects' | 'companies'
  from?: string
  to?: string
}

export const Route = createFileRoute('/reports')({
  // Дефолтный срез (сотрудники) и дефолтный период (текущий месяц) живут вне
  // URL — параметры появляются только у не-дефолтных, ссылка на /reports без
  // search валидна. Даты вне формата «YYYY-MM-DD» отбрасываются: период
  // возвращается к дефолту, а не роняет страницу.
  validateSearch: (search: Record<string, unknown> & SearchSchemaInput): ReportsSearch => ({
    ...(search.slice === 'projects' || search.slice === 'companies' ? { slice: search.slice } : {}),
    ...(isReportDate(search.from) ? { from: search.from } : {}),
    ...(isReportDate(search.to) ? { to: search.to } : {}),
  }),
  head: () => ({ meta: [{ title: 'Отчёты · STIT Телемост' }] }),
  component: ReportsPage,
})

function ReportsPage() {
  useRequireSession()
  const navigate = useNavigate()
  const search = Route.useSearch()
  const tab: ReportSliceName = search.slice ?? 'employees'
  // Дефолт считается один раз за жизнь страницы: «текущий месяц» не должен
  // переезжать под руками у открытой вкладки.
  const [defaultRange] = useState(currentMonthRange)
  const range = normalizeRange({
    from: search.from ?? defaultRange.from,
    to: search.to ?? defaultRange.to,
  })

  const setRange = (next: ReportRange) => {
    void navigate({
      to: '/reports',
      search: (prev: ReportsSearch) =>
        sameRange(next, defaultRange)
          ? { ...prev, from: undefined, to: undefined }
          : { ...prev, from: next.from, to: next.to },
    })
  }

  const employees = useQuery({
    ...employeeReportQueryOptions(range),
    enabled: tab === 'employees',
  })
  const projects = useQuery({ ...projectReportQueryOptions(range), enabled: tab === 'projects' })
  const companies = useQuery({ ...companyReportQueryOptions(range), enabled: tab === 'companies' })

  const active = tab === 'employees' ? employees : tab === 'projects' ? projects : companies
  const current = TABS.find((t) => t.value === tab) ?? TABS[0]

  const items =
    tab === 'employees'
      ? (employees.data?.rows ?? []).map(employeeItem)
      : tab === 'projects'
        ? (projects.data?.rows ?? []).map(projectItem)
        : (companies.data?.rows ?? []).map(companyItem)

  return (
    <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
      <PageHeader
        title="Отчёты"
        description="Сколько времени команда провела на созвонах — по сотрудникам, проектам и компаниям."
        actions={
          <>
            <PeriodPicker range={range} onChange={setRange} />
            <ExportButton slice={tab} range={range} disabled={items.length === 0} />
          </>
        }
      />

      <nav aria-label="Срез отчёта" className="mt-10 flex gap-5 border-border-subtle border-b">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            aria-pressed={t.value === tab}
            onClick={() =>
              void navigate({
                to: '/reports',
                search: t.value === 'employees' ? {} : { slice: t.value },
              })
            }
            className={cn(
              'relative pb-2 font-mono font-medium text-xs uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent',
              t.value === tab
                ? 'text-text-primary after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-accent'
                : 'text-text-tertiary hover:text-text-primary',
            )}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="mt-8">
        <ReportView
          items={items}
          subjectWord={[...current.word]}
          periodLabel={formatRangeLabel(range)}
          isPending={active.isLoading}
          isError={active.isError}
          onRetry={() => void active.refetch()}
          emptyTitle={current.empty}
        />
      </div>
    </div>
  )
}

// Встречи вне проекта и вне компании не выпадают из отчёта — они входят в итог
// строкой без привязки.
function projectLabel(row: ProjectReportRow): string {
  return row.projectName ?? row.projectId ?? 'Без проекта'
}

function companyLabel(row: CompanyReportRow): string {
  return row.companyName ?? row.companyId ?? 'Без компании'
}

function totalsOf(row: ReportTotals) {
  return {
    seconds: row.seconds,
    meetingCount: row.meetingCount,
    privateMeetingCount: row.privateMeetingCount,
  }
}

function projectItem(row: ProjectReportRow): ReportItem {
  return {
    key: `${row.companyId ?? ''}/${row.projectId ?? ''}`,
    title: projectLabel(row),
    note: row.companyName ?? row.companyId,
    ...totalsOf(row),
  }
}

function companyItem(row: CompanyReportRow): ReportItem {
  return {
    key: row.companyId ?? 'none',
    title: companyLabel(row),
    ...totalsOf(row),
  }
}

// Разбивка сотрудника по проектам и компаниям приезжает вместе со строкой —
// раскрытие не ходит на сервер.
function employeeItem(row: EmployeeReportRow): ReportItem {
  return {
    key: row.subject,
    title: row.fullName || row.subject,
    note: row.username ? `@${row.username}` : undefined,
    ...totalsOf(row),
    breakdown: (
      <div className="mt-3 grid gap-6 sm:grid-cols-2">
        <Breakdown
          title="По проектам"
          items={row.projects.map((p) => ({
            key: p.projectId ?? 'none',
            label: projectLabel(p),
            totals: p,
          }))}
        />
        <Breakdown
          title="По компаниям"
          items={row.companies.map((c) => ({
            key: c.companyId ?? 'none',
            label: companyLabel(c),
            totals: c,
          }))}
        />
      </div>
    ),
  }
}

function Breakdown({
  title,
  items,
}: {
  title: string
  items: { key: string; label: string; totals: ReportTotals }[]
}) {
  return (
    <div>
      <p className="label mb-2">{title}</p>
      <ul className="flex flex-col gap-1">
        {items.map((item) => (
          <li key={item.key} className="flex items-baseline justify-between gap-4 text-sm">
            <span className="min-w-0 truncate text-text-secondary">{item.label}</span>
            <span className="shrink-0 text-text-tertiary tabular-nums">
              {item.totals.meetingCount}&nbsp;
              {pluralRu(item.totals.meetingCount, 'встреча', 'встречи', 'встреч')} ·{' '}
              {formatCallTime(item.totals.seconds)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// Выгружается ровно то, что на экране: текущий срез за выбранный период. Файл
// собирает бэкенд — клиент отдаёт байты в скачивание под человекочитаемым именем.
function ExportButton({
  slice,
  range,
  disabled,
}: {
  slice: ReportSliceName
  range: ReportRange
  disabled: boolean
}) {
  const [saving, setSaving] = useState(false)
  return (
    <Button
      variant="secondary"
      size="sm"
      disabled={disabled || saving}
      onClick={() => {
        setSaving(true)
        fetchReportCsv(slice, range)
          .then((blob) => saveBlob(blob, reportCsvFilename(slice, range)))
          .catch(() => toast.error('Не удалось выгрузить отчёт. Повторите позже.'))
          .finally(() => setSaving(false))
      }}
    >
      <Icon name="download" size={16} />
      {saving ? 'Готовим…' : 'Выгрузить CSV'}
    </Button>
  )
}
